import { z } from 'zod';
import { FRONTEND_LOG_MAX_ISSUES, LOG_TOKEN_PATTERN, type IssueShape } from './frontend-logs';

export const JsonObjectSchema = z.record(z.string(), z.unknown());

function isPlainJsonObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * `JSON.parse` of text that must hold one JSON object, checking only that: no
 * per-key walk and no copy, which is what `JsonObjectSchema.parse` costs. For hot
 * paths that read back this process's own `JSON.stringify` output and validate
 * the fields with a row schema later. A non-object throws the same `ZodError`
 * as `JsonObjectSchema.parse`.
 */
export function parseJsonObjectEnvelope(text: string): Record<string, unknown> {
  const value: unknown = JSON.parse(text);
  return isPlainJsonObject(value) ? value : JsonObjectSchema.parse(value);
}

const MAX_ISSUES_IN_MESSAGE = 3;
/** `LoggedIssueSchema.path` bound. */
const MAX_PATH_LENGTH = 400;

type IssueLike = {
  code?: string;
  path?: ReadonlyArray<PropertyKey>;
  expected?: unknown;
  format?: unknown;
  origin?: unknown;
};

/** Placeholder for a path segment that may be DATA (a record key) rather than a schema field name. */
const DYNAMIC_KEY_SEGMENT = '{key}';
/** Display text for an array index. */
const INDEX_SEGMENT = '[]';

const SAFE_FIELD_NAME = /^[A-Za-z_$][A-Za-z0-9_$-]{0,59}$/;

/** `v` when it is a schema-vocabulary token (see `LoggedIssueSchema`), else undefined. */
function tokenOrUndefined(v: unknown): string | undefined {
  return typeof v === 'string' && LOG_TOKEN_PATTERN.test(v) ? v : undefined;
}

/**
 * True when the issue's LAST path segment is derivably a record key (data),
 * not a schema field. A record's `invalid_key` carries `path: [..., key]`. A
 * MAP's `invalid_key`/`invalid_element` sits at the map CONTAINER path (the
 * key is not in the path), so masking there would erase the field name and
 * merge separate map fields: never mask for `origin: 'map'`. Masking lives
 * here, in the browser, because only here is the issue's `origin` known.
 */
function isRecordKeyIssue(code: unknown, origin: unknown): boolean {
  if (origin === 'map') return false;
  if (code === 'invalid_key') return true;
  return (code === 'invalid_element' || code === 'invalid_value') && origin === 'record';
}

/** Typed path segment used for grouping: a field name (string) or an index (`null`). */
type TypedSegment = string | null;

/**
 * A path segment is logged only when it looks like a schema field name.
 * Anything else (emails, ids, dotted keys, long text) is a record key the
 * client's DATA chose, and is replaced by a placeholder.
 */
function typedSegments(issue: IssueLike): TypedSegment[] {
  const raw = (issue.path ?? []).map((seg): TypedSegment => {
    if (typeof seg === 'number') return null;
    const name = String(seg);
    return SAFE_FIELD_NAME.test(name) ? name : DYNAMIC_KEY_SEGMENT;
  });
  // A record-key failure's last segment IS the offending key.
  if (isRecordKeyIssue(issue.code, issue.origin) && raw.length > 0) raw[raw.length - 1] = DYNAMIC_KEY_SEGMENT;
  return raw;
}

function displayPath(segments: ReadonlyArray<TypedSegment>): string {
  const path = segments.map((s) => (s === null ? INDEX_SEGMENT : s)).join('.') || '(root)';
  return path.length > MAX_PATH_LENGTH ? `${path.slice(0, MAX_PATH_LENGTH - 3)}...` : path;
}

/** Human description of a shape built from safe metadata only. */
export function describeShape(shape: Pick<IssueShape, 'code' | 'path' | 'expected' | 'format'>): string {
  const detail = [shape.expected ? `expected ${shape.expected}` : '', shape.format ? `format ${shape.format}` : '']
    .filter(Boolean)
    .join(', ');
  return `${shape.path}: ${shape.code}${detail ? ` (${detail})` : ''}`;
}

export function truncatedMessage(count: number): string {
  return `and ${count} more shapes`;
}

export function isTruncatedShape(s: IssueShape): boolean {
  return s.code === 'truncated';
}

function describeIssues(issues: z.core.$ZodIssue[]): string {
  const shown = summarizeIssues(issues.slice(0, MAX_ISSUES_IN_MESSAGE), MAX_ISSUES_IN_MESSAGE)
    .map((s) => (isTruncatedShape(s) ? truncatedMessage(s.count) : describeShape(s)))
    .join('; ');
  const extra = issues.length - MAX_ISSUES_IN_MESSAGE;
  // Bounded without a cut: at most 3 shapes, each path capped at MAX_PATH_LENGTH.
  return extra > 0 ? `${shown}; and ${extra} more` : shown;
}

/**
 * Collapse a (possibly huge) issue list into distinct failure shapes. A list
 * response that fails on every row yields one issue per row per field; the
 * shape (code + typed path with indices collapsed) is what is diagnostic.
 *
 * Output is built from safe metadata ONLY: the issue `code`, the expected type
 * name and the schema path. Zod's free-text `message` (which can embed the
 * offending value, e.g. `Unrecognized key: "someone@example.com"`) and
 * `unrecognized_keys.keys` are never copied. At most `max` shapes are
 * returned, plus a trailing `truncated` marker when more existed.
 *
 * Residual (a conscious decision, pinned by a test): record keys are masked
 * only where the issue itself says so (`invalid_key`, record/map-origin
 * element/value errors). An identifier-shaped record key that fails as a
 * VALUE error under a record schema (`{ byType: { customer_secret: {} } }` ->
 * `byType.customer_secret.id`) is still logged as a path segment: Zod gives no
 * signal that the segment is data. These are internal field names, never
 * message text; non-identifier keys (emails, ids, dotted text) are masked.
 */
export function summarizeIssues(issues: ReadonlyArray<IssueLike>, max: number = FRONTEND_LOG_MAX_ISSUES): IssueShape[] {
  const shapes = new Map<string, IssueShape>();
  for (const issue of issues) {
    const segments = typedSegments(issue);
    // Token-shaped or nothing: what the collector's `LoggedIssueSchema` will accept.
    const code = tokenOrUndefined(issue.code) ?? 'unknown';
    const expected = tokenOrUndefined(issue.expected);
    const format = tokenOrUndefined(issue.format);
    // Group by an encoded array of typed segments so `['a.b']`, `['a','b']`
    // and `['items', 0]` vs `['items', '[]']` can never merge.
    const key = JSON.stringify([code, expected ?? null, format ?? null, segments]);
    const existing = shapes.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      shapes.set(key, {
        code,
        path: displayPath(segments),
        count: 1,
        ...(expected !== undefined ? { expected } : {}),
        ...(format !== undefined ? { format } : {}),
      });
    }
  }
  const all = [...shapes.values()];
  if (all.length <= max) return all;
  return [...all.slice(0, max), { code: 'truncated', path: '', count: all.length - max }];
}

export class JsonParseError extends Error {
  readonly kind: 'syntax' | 'shape' | 'read';
  readonly issues: z.core.$ZodIssue[];
  declare readonly cause?: unknown;

  constructor(kind: 'syntax' | 'shape' | 'read', message: string, issues: z.core.$ZodIssue[] = [], cause?: unknown) {
    super(message);
    if (cause !== undefined) Object.defineProperty(this, 'cause', { value: cause, configurable: true, writable: true });
    this.name = 'JsonParseError';
    this.kind = kind;
    this.issues = issues;
  }
}

export type SafeParseJsonResult<T> =
  | { success: true; data: T }
  | { success: false; error: JsonParseError };

export function safeParseJson<S extends z.ZodType>(
  schema: S,
  text: string,
): SafeParseJsonResult<z.infer<S>> {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (err) {
    // The engine's message quotes a snippet of the input; keep it in `cause`, not the message.
    return { success: false, error: new JsonParseError('syntax', 'Invalid JSON (syntax error)', [], err) };
  }
  const result = schema.safeParse(raw);
  if (!result.success) {
    return {
      success: false,
      error: new JsonParseError('shape', `JSON does not match schema: ${describeIssues(result.error.issues)}`, result.error.issues),
    };
  }
  return { success: true, data: result.data };
}

export function parseJson<S extends z.ZodType>(schema: S, text: string): z.infer<S> {
  const result = safeParseJson(schema, text);
  if (!result.success) throw result.error;
  return result.data;
}

/**
 * Reads a fetch `Response` body as text and parses it through `schema`. Never
 * throws: a body that cannot be read (aborted, already consumed) is a
 * `kind: 'read'` failure carrying the original error as `cause`. Pass
 * `res.clone()` to leave the caller's response unconsumed.
 */
export async function safeParseResponse<S extends z.ZodType>(
  schema: S,
  res: Response,
): Promise<SafeParseJsonResult<z.infer<S>>> {
  let text: string;
  try {
    text = await res.text();
  } catch (err) {
    return { success: false, error: new JsonParseError('read', 'Could not read response body', [], err) };
  }
  return safeParseJson(schema, text);
}
